import { useEffect, useState } from 'react';
import { Key, Copy, Trash2, Plus, Loader2, Check, AlertTriangle } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import { useToast } from '@/hooks/useToast';

interface ApiKeyItem {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  createdAt: string;
}

interface ApiKeyCreateResult {
  key: string;
  apiKey: ApiKeyItem;
}

export function ApiKeysPage() {
  const toast = useToast();
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    apiGet<ApiKeyItem[]>('/settings/api-keys')
      .then(setKeys)
      .catch(() => toast.error('Failed to load API keys'))
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const result = await apiPost<ApiKeyCreateResult>('/settings/api-keys', { name: newKeyName.trim() });
      setKeys((prev) => [result.apiKey, ...prev]);
      setRevealedKey(result.key);
      setNewKeyName('');
      toast.success('API key created');
    } catch {
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: string) {
    setRevoking(id);
    try {
      await apiDelete(`/settings/api-keys/${id}`);
      setKeys((prev) => prev.filter((k) => k.id !== id));
      toast.success('API key revoked');
    } catch {
      toast.error('Failed to revoke API key');
    } finally {
      setRevoking(null);
    }
  }

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function closeRevealModal() {
    setRevealedKey(null);
    setShowCreateModal(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-2xl mx-auto w-full animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight">API Keys</h1>
        <p className="text-sm text-base-content/40 mt-1">
          Create and manage API keys for the HyPeMa public API.
        </p>
      </div>

      {/* Create button */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="btn btn-primary btn-sm gap-2"
      >
        <Plus size={16} />
        Create API Key
      </button>

      {/* Keys list */}
      <div className="space-y-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
          Active Keys
        </h2>
        {keys.length === 0 ? (
          <div className="glass-card rounded-xl p-8 text-center">
            <Key size={32} className="mx-auto text-base-content/20 mb-3" />
            <p className="text-sm text-base-content/40">No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="glass-card rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base-300">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-base-content/40">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-base-content/40">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-base-content/40">Last Used</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-base-content/40">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-base-content/40"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-300/50">
                {keys.map((key) => (
                  <tr key={key.id} className="hover:bg-base-200/30 transition-colors">
                    <td className="px-4 py-3 font-medium">{key.name}</td>
                    <td className="px-4 py-3">
                      <code className="text-xs font-mono bg-base-200/50 px-2 py-0.5 rounded">
                        {key.keyPrefix}...
                      </code>
                    </td>
                    <td className="px-4 py-3 text-xs text-base-content/60">
                      {key.lastUsedAt
                        ? new Date(key.lastUsedAt).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-xs text-base-content/60">
                      {new Date(key.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRevoke(key.id)}
                        disabled={revoking === key.id}
                        className="btn btn-xs btn-ghost text-error hover:bg-error/10"
                        title="Revoke key"
                      >
                        {revoking === key.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && !revealedKey && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg mb-4">Create API Key</h3>
            <div className="form-control">
              <label className="label">
                <span className="label-text text-sm">Key name</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Production, Staging, My App"
                className="input input-bordered w-full"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
            </div>
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={closeRevealModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary gap-2"
                onClick={handleCreate}
                disabled={creating || !newKeyName.trim()}
              >
                {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Create
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={closeRevealModal}>close</button>
          </form>
        </dialog>
      )}

      {/* Reveal Key Modal */}
      {revealedKey && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-lg">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={20} className="text-warning" />
              <h3 className="font-bold text-lg">Save your API key</h3>
            </div>
            <p className="text-sm text-base-content/60 mb-4">
              This is the only time you'll see the full key. Copy it now and store it securely.
            </p>
            <div className="flex items-center gap-2 bg-base-200 rounded-lg p-3">
              <code className="text-xs font-mono flex-1 break-all select-all">
                {revealedKey}
              </code>
              <button
                className="btn btn-sm btn-ghost shrink-0"
                onClick={() => handleCopy(revealedKey)}
              >
                {copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
              </button>
            </div>
            <div className="modal-action">
              <button className="btn btn-primary" onClick={closeRevealModal}>
                Done
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
}
